import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../base/Button';
import { useAuth } from '../../context/AuthContext';

export default function Header() {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { user, signOut, isAdmin, role } = useAuth();
  const navigate = useNavigate();

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const lang = e.target.value;
    i18n.changeLanguage(lang);
  };

  const handleSignInClick = () => {
    navigate('/auth/login');
  };

  const handleDashboardClick = () => {
    navigate('/admin');
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <>
      <header className="bg-black shadow-sm border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/3d693276a5424479ffa5f1cc868e7c58/82df335f8c41874f05b0235d813bec86.png"
                alt="Rigland Travels Ltd"
                className="h-12 w-auto"
              />
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              <Link to="/general?tab=flights" className="text-gray-300 hover:text-logo-gold-400 transition-colors whitespace-nowrap text-sm font-medium">
                {t('nav.flights')}
              </Link>
              <Link to="/general?tab=hotels" className="text-gray-300 hover:text-logo-gold-400 transition-colors whitespace-nowrap text-sm font-medium">
                {t('nav.hotels')}
              </Link>
              <Link to="/search?type=cars" className="text-gray-300 hover:text-logo-gold-400 transition-colors whitespace-nowrap text-sm font-medium">
                {t('nav.taxi')}
              </Link>
              <Link to="/search?type=packages" className="text-gray-300 hover:text-logo-gold-400 transition-colors whitespace-nowrap text-sm font-medium">
                {t('nav.packages')}
              </Link>
              <Link to="/search?type=aiBooking" className="text-gray-300 hover:text-logo-gold-400 transition-colors whitespace-nowrap text-sm font-medium">
                {t('nav.aiBooking')}
              </Link>
              <Link to="/hajj-umrah" className="text-gray-300 hover:text-logo-gold-400 transition-colors whitespace-nowrap text-sm font-medium">
                {t('nav.hajjUmrah')}
              </Link>
            </nav>

            {/* Right side actions */}
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-2">
                <i className="ri-global-line text-gray-300 text-sm"></i>
                <select
                  className="text-xs text-gray-300 bg-transparent border-none cursor-pointer pr-6"
                  value={i18n.language}
                  onChange={handleLanguageChange}
                >
                  <option value="en">English (US)</option>
                  <option value="fr">Français</option>
                  <option value="ha">Hausa</option>
                  <option value="ar">العربية</option>
                </select>
              </div>

              <div className="hidden md:flex items-center space-x-2">
                <i className="ri-money-dollar-circle-line text-gray-300"></i>
                <select className="text-sm text-gray-300 bg-transparent border-none cursor-pointer pr-8">
                  <option>USD</option>
                  <option>EUR</option>
                  <option>GBP</option>
                  <option>NGN</option>
                </select>
              </div>

              {user ? (
                <div className="relative hidden md:flex items-center ml-auto">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center text-gray-300 hover:text-logo-gold-400 focus:outline-none"
                  >
                    <i className="ri-user-line mr-1 text-lg"></i>
                    <span className="text-sm font-medium">{user.email?.split('@')[0]}</span>
                    <i className={`ri-arrow-down-s-line ml-1 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50 border border-gray-100">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
                        <p className="text-xs text-gray-500 capitalize">{role || 'User'}</p>
                      </div>

                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <i className="ri-dashboard-line mr-2 align-middle"></i>
                          Admin Dashboard
                        </Link>
                      )}

                      {!isAdmin && (
                        <Link
                          to="/dashboard"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <i className="ri-dashboard-line mr-2 align-middle"></i>
                          My Dashboard
                        </Link>
                      )}

                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <i className="ri-user-settings-line mr-2 align-middle"></i>
                        My Profile
                      </Link>

                      <button
                        onClick={() => {
                          signOut();
                          setIsDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <i className="ri-logout-box-line mr-2 align-middle"></i>
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSignInClick}
                  className="hidden md:inline-flex text-gray-300 hover:text-logo-gold-400"
                >
                  {t('nav.signIn')}
                </Button>
              )}

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 rounded-md text-gray-300 hover:text-logo-gold-400 hover:bg-gray-800 cursor-pointer"
              >
                <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-black border-t border-gray-800">
            <div className="px-4 py-2 space-y-1">
              <Link
                to="/general?tab=flights"
                className="block px-4 py-2 text-gray-300 hover:text-logo-gold-400 hover:bg-gray-900 rounded-lg"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.flights')}
              </Link>
              <Link
                to="/general?tab=hotels"
                className="block px-4 py-2 text-gray-300 hover:text-logo-gold-400 hover:bg-gray-900 rounded-lg"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.hotels')}
              </Link>
              <Link
                to="/search?type=cars"
                className="block px-4 py-2 text-gray-300 hover:text-logo-gold-400 hover:bg-gray-900 rounded-lg"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.taxi')}
              </Link>
              <Link
                to="/search?type=packages"
                className="block px-4 py-2 text-gray-300 hover:text-logo-gold-400 hover:bg-gray-900 rounded-lg"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.packages')}
              </Link>
              <Link
                to="/search?type=aiBooking"
                className="block px-4 py-2 text-gray-300 hover:text-logo-gold-400 hover:bg-gray-900 rounded-lg"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.aiBooking')}
              </Link>
              <Link
                to="/hajj-umrah"
                className="block px-4 py-2 text-gray-300 hover:text-logo-gold-400 hover:bg-gray-900 rounded-lg"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.hajjUmrah')}
              </Link>
              
              <div className="border-t border-gray-800 my-2 pt-2">
                {user ? (
                  <>
                    <Link
                      to={isAdmin ? "/admin" : "/dashboard"}
                      className="block px-4 py-2 text-gray-300 hover:text-logo-gold-400 hover:bg-gray-900 rounded-lg"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="ri-dashboard-line mr-2"></i> Dashboard
                    </Link>
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-gray-300 hover:text-logo-gold-400 hover:bg-gray-900 rounded-lg"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="ri-user-settings-line mr-2"></i> Profile
                    </Link>
                    <button
                      onClick={() => {
                        signOut();
                        setIsMenuOpen(false);
                      }}
                      className="w-full text-left block px-4 py-2 text-red-500 hover:bg-red-500/10 rounded-lg"
                    >
                      <i className="ri-logout-box-line mr-2"></i> Sign Out
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      handleSignInClick();
                      setIsMenuOpen(false);
                    }}
                    className="w-full text-left block px-4 py-2 text-logo-gold-400 hover:bg-logo-gold-400/10 rounded-lg font-medium"
                  >
                    <i className="ri-login-box-line mr-2"></i> {t('nav.signIn')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}